import type { API, InlineTool } from '@editorjs/editorjs';

import { SPAN_SANITIZE } from '@/editorjs-tools/sanitize-config';

export type ReferenceConfig = {
  onPickerOpen: (range: Range) => void;
};

const SVG_NS = 'http://www.w3.org/2000/svg';

function buildLinkIcon(): SVGSVGElement {
  const svg = document.createElementNS(SVG_NS, 'svg');
  svg.setAttribute('width', '14');
  svg.setAttribute('height', '14');
  svg.setAttribute('viewBox', '0 0 24 24');
  svg.setAttribute('fill', 'none');
  svg.setAttribute('stroke', 'currentColor');
  svg.setAttribute('stroke-width', '2');
  svg.setAttribute('stroke-linecap', 'round');
  svg.setAttribute('stroke-linejoin', 'round');

  const path1 = document.createElementNS(SVG_NS, 'path');
  path1.setAttribute(
    'd',
    'M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71',
  );
  const path2 = document.createElementNS(SVG_NS, 'path');
  path2.setAttribute(
    'd',
    'M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71',
  );

  svg.appendChild(path1);
  svg.appendChild(path2);
  return svg;
}

export class ReferenceTool implements InlineTool {
  static isInline = true as const;
  static title = 'Reference';

  static get sanitize() {
    return { span: SPAN_SANITIZE };
  }

  // Used in Task 7 wiring (toolbar close + selection helpers).
  private api: API;
  private config: ReferenceConfig;
  // Set in render(); referenced by Task 7 chip-insert effect.
  private button: HTMLButtonElement | null = null;

  constructor(args: { api: API; config: ReferenceConfig }) {
    this.api = args.api;
    this.config = args.config;
    // api and button are used in Task 7 wiring; suppress noUnusedLocals.
     
    void (this.api, this.button);
  }

  render(): HTMLButtonElement {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'ce-inline-tool ce-inline-tool--reference';
    button.setAttribute('aria-label', 'Insert reference');
    button.appendChild(buildLinkIcon());
    this.button = button;
    return button;
  }

  surround(range: Range): void {
    if (range.collapsed) return;
    this.config.onPickerOpen(range);
  }

  checkState(selection: Selection): boolean {
    // EditorJS passes the current document Selection (NOT a Range — the
    // base InlineTool interface from @editorjs/editorjs uses Selection).
    // Return true on a non-collapsed selection so the button highlights.
    // The button stays clickable in either state — surround() short-
    // circuits on a collapsed range, so a misclick is a no-op.
    return !selection.isCollapsed;
  }
}
